import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect } from 'react';

import { gameResourcesApi } from '@shared/api';
import { getGameResourceOwner, useGameSession } from '@shared/lib/auth/game-session';

import { developmentMode } from './development-mode';

const lifeRecoveryMs = 8 * 60 * 60 * 1000;
const maxLives = 3;
export const lifeRefillPrice = 100;
export const fiftyFiftyPrice = 50;
const uznabucksPlural = new Intl.PluralRules('ru');
const uznabucksForms = {
  zero: 'узнабаксов',
  one: 'узнабакс',
  two: 'узнабакса',
  few: 'узнабакса',
  many: 'узнабаксов',
  other: 'узнабакса',
} as const;
export const formatUznabucks = (amount: number) => `${amount} ${uznabucksForms[uznabucksPlural.select(amount)]}`;
const storageKey = (login?: string) => `uznali:game-resources:v1:${login ?? 'guest'}`;

export type GameResources = {
  lives: number;
  nextLifeAt: number | null;
  fiftyFifty: number;
  swapFrame: number;
  freeze: number;
  coins: number;
  claimedCampaignChapters: string[];
};

const initialResources = (): GameResources => ({
  lives: 2,
  nextLifeAt: Date.now() + lifeRecoveryMs,
  fiftyFifty: 2,
  swapFrame: 0,
  freeze: 0,
  coins: 200,
  claimedCampaignChapters: [],
});

const recoverLives = (resources: GameResources, now = Date.now()): GameResources => {
  if (resources.lives >= maxLives || resources.nextLifeAt === null) {
    return { ...resources, lives: Math.min(maxLives, resources.lives), nextLifeAt: null };
  }
  if (now < resources.nextLifeAt) return resources;
  const recovered = Math.floor((now - resources.nextLifeAt) / lifeRecoveryMs) + 1;
  const lives = Math.min(maxLives, resources.lives + recovered);
  return {
    ...resources,
    lives,
    nextLifeAt: lives === maxLives ? null : resources.nextLifeAt + recovered * lifeRecoveryMs,
  };
};

export const readGameResources = (login?: string): GameResources => {
  try {
    const raw = window.localStorage.getItem(storageKey(login));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    const value = parsed && typeof parsed === 'object' ? (parsed as Partial<GameResources>) : null;
    const resources =
      value && Number.isInteger(value.lives) && Number.isInteger(value.fiftyFifty)
        ? {
            lives: Math.max(0, Math.min(maxLives, value.lives ?? maxLives)),
            nextLifeAt: typeof value.nextLifeAt === 'number' ? value.nextLifeAt : null,
            fiftyFifty: Math.max(0, value.fiftyFifty ?? 0),
            swapFrame: Number.isInteger(value.swapFrame) ? Math.max(0, value.swapFrame ?? 0) : 0,
            freeze: Number.isInteger(value.freeze) ? Math.max(0, value.freeze ?? 0) : 0,
            coins: Number.isInteger(value.coins) ? Math.max(0, value.coins ?? 0) : 200,
            claimedCampaignChapters: Array.isArray(value.claimedCampaignChapters)
              ? value.claimedCampaignChapters.filter((key): key is string => typeof key === 'string')
              : [],
          }
        : initialResources();

    return login ? { ...resources, lives: 0, nextLifeAt: null, coins: 0 } : recoverLives(resources);
  } catch {
    const initial = initialResources();
    return login ? { ...initial, lives: 0, nextLifeAt: null, coins: 0 } : initial;
  }
};

class GameResourcesStore {
  maxLives = maxLives;
  loaded: boolean;
  loading = false;
  error: string | null = null;
  private request: Promise<boolean> | null = null;
  private syncedAt = 0;
  private recoveryCheckedAt = 0;
  private serverRevision = 0;
  lives: number;
  nextLifeAt: number | null;
  fiftyFifty: number;
  swapFrame: number;
  freeze: number;
  coins: number;
  claimedCampaignChapters: string[];

  get canPlay() {
    return developmentMode.enabled || (this.loaded && this.lives > 0);
  }

  constructor(private readonly login?: string) {
    const initial: GameResources = {
      lives: 0,
      nextLifeAt: null,
      fiftyFifty: 0,
      swapFrame: 0,
      freeze: 0,
      coins: 0,
      claimedCampaignChapters: [],
    };
    this.lives = initial.lives;
    this.nextLifeAt = initial.nextLifeAt;
    this.fiftyFifty = initial.fiftyFifty;
    this.swapFrame = initial.swapFrame;
    this.freeze = initial.freeze;
    this.coins = initial.coins;
    this.claimedCampaignChapters = initial.claimedCampaignChapters;
    this.loaded = false;
    makeAutoObservable<this, 'request' | 'syncedAt' | 'recoveryCheckedAt' | 'serverRevision'>(
      this,
      {
        request: false,
        syncedAt: false,
        recoveryCheckedAt: false,
        serverRevision: false,
      },
      { autoBind: true },
    );

    if (typeof window !== 'undefined') window.addEventListener('storage', this.onStorage);
  }

  get snapshot(): GameResources {
    return {
      lives: this.lives,
      nextLifeAt: this.nextLifeAt,
      fiftyFifty: this.fiftyFifty,
      swapFrame: this.swapFrame,
      freeze: this.freeze,
      coins: this.coins,
      claimedCampaignChapters: [...this.claimedCampaignChapters],
    };
  }

  private apply(resources: GameResources) {
    this.lives = resources.lives;
    this.nextLifeAt = resources.nextLifeAt;
    this.fiftyFifty = resources.fiftyFifty;
    this.swapFrame = resources.swapFrame;
    this.freeze = resources.freeze;
    this.coins = resources.coins;
    this.claimedCampaignChapters = resources.claimedCampaignChapters;
  }

  private onStorage = (event: StorageEvent) => {
    if (event.storageArea === window.localStorage && event.key === storageKey(this.login)) {
      if (this.login) void this.refresh(true);
      else this.apply(readGameResources());
    }
  };

  refresh = (force = false): Promise<boolean> => {
    if (!this.login || getGameResourceOwner() !== this.login) return Promise.resolve(false);
    if (this.request) return this.request;
    if (!force && this.loaded && Date.now() - this.syncedAt < 15_000) return Promise.resolve(true);
    this.loading = true;
    this.error = null;
    const login = this.login;
    const revision = this.serverRevision;
    this.request = gameResourcesApi
      .get()
      .then((progress) => {
        if (getGameResourceOwner() !== login || revision !== this.serverRevision) return false;
        runInAction(() => {
          this.lives = progress.lives;
          this.maxLives = progress.maxLives;
          this.nextLifeAt = progress.nextLifeAt;
          this.coins = progress.coins;
          this.fiftyFifty = progress.inventory.fiftyFifty;
          this.swapFrame = progress.inventory.swapFrame;
          this.freeze = progress.inventory.freeze;
          this.loaded = true;
          this.syncedAt = Date.now();
        });
        return true;
      })
      .catch(() => {
        runInAction(() => {
          this.error = 'Не удалось обновить жизни. Попробуйте ещё раз.';
        });
        return false;
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
          this.request = null;
        });
      });
    return this.request;
  };

  applyCampaignResources(progress: {
    coins: number;
    exp: number;
    lives: number;
    maxLives: number;
    inventory: { fiftyFifty: number; swapFrame: number; freeze: number };
    nextLifeAt?: string | null;
  }) {
    if (!this.login || getGameResourceOwner() !== this.login) return;
    this.serverRevision++;
    this.syncedAt = Date.now();
    this.error = null;
    this.coins = progress.coins;
    this.lives = progress.lives;
    this.maxLives = progress.maxLives;
    this.fiftyFifty = progress.inventory.fiftyFifty;
    this.swapFrame = progress.inventory.swapFrame;
    this.freeze = progress.inventory.freeze;
    if (progress.nextLifeAt !== undefined)
      this.nextLifeAt = progress.nextLifeAt ? Date.parse(progress.nextLifeAt) : null;
    if (this.lives >= this.maxLives) this.nextLifeAt = null;
    this.loaded = true;
  }

  async refreshAfterMutation() {
    await this.request;
    return this.refresh(true);
  }

  recover = () => {
    if (this.login) {
      if (this.nextLifeAt !== null && Date.now() >= this.nextLifeAt && Date.now() - this.recoveryCheckedAt >= 10_000) {
        this.recoveryCheckedAt = Date.now();
        void this.refresh(true);
      }
      return;
    }
    const next = recoverLives(this.snapshot);
    if (next.lives !== this.lives || next.nextLifeAt !== this.nextLifeAt) this.apply(next);
  };

  save(resources: GameResources) {
    this.apply(resources);
    try {
      window.localStorage.setItem(storageKey(this.login), JSON.stringify(resources));
    } catch {
      // The current visit keeps the updated resources in memory.
    }
  }
}

const stores = new Map<string, GameResourcesStore>();
let recoveryTimer: number | null = null;
const getStore = (login?: string) => {
  const owner = getGameResourceOwner();
  login = owner?.startsWith('guest:') ? owner : (login ?? owner);
  const key = login ?? 'guest';
  let store = stores.get(key);
  if (!store) {
    store = new GameResourcesStore(login);
    stores.set(key, store);
    if (recoveryTimer === null && typeof window !== 'undefined') {
      recoveryTimer = window.setInterval(() => {
        if (document.visibilityState === 'hidden') return;
        stores.forEach((entry) => {
          entry.recover();
          void entry.refresh();
        });
      }, 60_000);
      window.addEventListener('focus', () =>
        stores.forEach((entry) => {
          void entry.refresh();
        }),
      );
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible')
          stores.forEach((entry) => {
            void entry.refresh();
          });
      });
    }
  }
  return store;
};

export const claimCampaignReward = (login: string | undefined, chapterKey: string, amount: number) => {
  if (!chapterKey || !Number.isInteger(amount) || amount <= 0) return;
  const store = getStore(login);
  if (login || getGameResourceOwner()) return;
  const current = store.snapshot;
  if (current.claimedCampaignChapters.includes(chapterKey)) return;
  store.save({
    ...current,
    coins: current.coins + amount,
    claimedCampaignChapters: [...current.claimedCampaignChapters, chapterKey],
  });
};

export const spendLife = (login?: string): GameResources | null => {
  const store = getStore(login);
  const current = store.snapshot;
  if (developmentMode.enabled) return current;
  if (current.lives === 0) return null;
  // Demonstration questions cannot spend authoritative server lives.
  if (login || getGameResourceOwner()) return current;
  const next = {
    ...current,
    lives: current.lives - 1,
    nextLifeAt: current.nextLifeAt ?? Date.now() + lifeRecoveryMs,
  };
  store.save(next);
  return next;
};

export const spendFiftyFifty = (login?: string): GameResources | null => {
  const store = getStore(login);
  const current = store.snapshot;
  if (developmentMode.enabled) return current;
  if (current.fiftyFifty === 0) return null;
  if (login || getGameResourceOwner()) return current;
  const next = { ...current, fiftyFifty: current.fiftyFifty - 1 };
  store.save(next);
  return next;
};

export const buyLife = (login?: string): GameResources | null => {
  if (login || getGameResourceOwner()) return null;
  const store = getStore(login);
  const current = store.snapshot;
  if (current.lives >= maxLives || current.coins < lifeRefillPrice) return null;
  const lives = current.lives + 1;
  const next = {
    ...current,
    lives,
    nextLifeAt: lives === maxLives ? null : (current.nextLifeAt ?? Date.now() + lifeRecoveryMs),
    coins: current.coins - lifeRefillPrice,
  };
  store.save(next);
  return next;
};

export const buyFiftyFifty = (login?: string): GameResources | null => {
  if (login || getGameResourceOwner()) return null;
  const store = getStore(login);
  const current = store.snapshot;
  if (current.coins < fiftyFiftyPrice) return null;
  const next = { ...current, coins: current.coins - fiftyFiftyPrice, fiftyFifty: current.fiftyFifty + 1 };
  store.save(next);
  return next;
};

export const useGameResources = (login?: string) => {
  useGameSession();
  const store = getStore(login);
  useEffect(() => {
    void store.refresh();
  }, [store]);
  return store;
};

export const lifeRecoveryLabel = (nextLifeAt: number | null, now = Date.now()) => {
  if (nextLifeAt === null) return '';
  const minutes = Math.max(0, Math.ceil((nextLifeAt - now) / 60_000));
  const hours = Math.floor(minutes / 60);
  return `${hours} ч ${String(minutes % 60).padStart(2, '0')} мин`;
};
